import React, { useState, useEffect, useMemo } from 'react';
import { CarbonCareDatePicker, parseIsoDate } from '../components/shared/CarbonCareDatePicker';
import countriesLib from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import deLocale from 'i18n-iso-countries/langs/de.json';
import frLocale from 'i18n-iso-countries/langs/fr.json';
import esLocale from 'i18n-iso-countries/langs/es.json';
import itLocale from 'i18n-iso-countries/langs/it.json';
import ptLocale from 'i18n-iso-countries/langs/pt.json';
import ruLocale from 'i18n-iso-countries/langs/ru.json';
import zhLocale from 'i18n-iso-countries/langs/zh.json';
import jaLocale from 'i18n-iso-countries/langs/ja.json';
import arLocale from 'i18n-iso-countries/langs/ar.json';
import koLocale from 'i18n-iso-countries/langs/ko.json';
import nlLocale from 'i18n-iso-countries/langs/nl.json';
import plLocale from 'i18n-iso-countries/langs/pl.json';
import trLocale from 'i18n-iso-countries/langs/tr.json';
import svLocale from 'i18n-iso-countries/langs/sv.json';
import hiLocale from 'i18n-iso-countries/langs/hi.json';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Paper,
  FormHelperText,
  Tooltip,
  Autocomplete,
  LinearProgress,
  CircularProgress,
  Avatar,
  InputAdornment,
  IconButton,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Skeleton,
 Grid } from '@mui/material';
import { CheckCircle, Cancel, LocationOn, Business, Delete , CloudUpload ,
  Add,
  Edit,
  KeyOutlined,
  ToggleOn,
  ToggleOff,
  Person,
  Save,
  Search,
  CalendarToday,
  Numbers,
  Visibility,
  FileDownload,
  FilterList,
} from '@mui/icons-material';
import { Company, CreateCompanyData, CustomerType, LicenceType } from '../types/company';
import { companyApi, LicenceStatsResponse, CompanyStatusFilter } from '../services/api/companyApi';

import UserManager from '../components/company/UserManager';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import PageHeader from '../components/common/PageHeader';
import StatusFilterDropdown from './_shared/StatusFilterDropdown';

// Default values for a brand-new company form. Used for the initial form state
// and when resetting the form back to its pristine state.
const createInitialFormData = (): CreateCompanyData => ({
  // Basic Info
  name: '',

  // Address
  addressStreet: '',
  addressCity: '',
  addressPostalCode: '',
  addressCountry: '',
  addressState: '',

  // Business Details
  logoUrl: '',

  // Configuration
  customerType: CustomerType.WebUser,
  partnerName: '',

  // Business Information



  // Licensing
  licenceType: LicenceType.Basic,
  isDemo: false,
  analytics: true,
  calculationsAllotted: 1_000_000,
  calculationsLeft: undefined,

  // Limits and Counters
  totalNumCalculations: 0,
  totalNumGeoLocs: 0,
  geolocationAllotted: 10_000, // 1% of calculationsAllotted
  geolocationLeft: undefined,
  addedShipments: 0,
  addedGeoLimit: 0,

  // Unique Shipments
  uniqueShipmentsAllotted: 1_000_000,
  uniqueShipmentsLeft: undefined,
  totalNumUniqueShipments: 0,
  addedUniqueShipments: 0,

  // Features and Status
  isUnlimited: false,
  certificateAvailable: false,
  disabled: false,
  resellerId: undefined,
  showLegBreakdown: false,

  // Certificate Text Fields
  upperText: 'in partnership with CarbonCare, has successfully analysed and calculated the emissions. Such calculations have been conducted in accordance with the provisions set forth under',
  bottomText: 'This achievement highlights a strong commitment by both organizations to environmental responsibility, transparency and sustainable logistics practices. Their efforts set a valuable benchmark for emissions accountability within the transport and logistics sector.',
});

// Maps a fully-loaded company (from the API, with possible snake_case/camelCase
// variants) into the shape used by the company form. Shared by the edit and view flows.
const mapCompanyToFormData = (fullCompany: Company): CreateCompanyData => ({
  // Basic Info
  name: fullCompany.name || '',

  // Address
  addressStreet: fullCompany.addressStreet || fullCompany.address_street || '',
  addressCity: fullCompany.addressCity || fullCompany.address_city || '',
  addressPostalCode: fullCompany.addressPostalCode || fullCompany.address_postal_code || '',
  addressCountry: fullCompany.addressCountry || fullCompany.address_country || '',
  addressState: fullCompany.addressState || fullCompany.address_state || '',

  // Business Details
  logoUrl: fullCompany.logoUrl || fullCompany.logo_url || '',

  // Configuration
  customerType: fullCompany.customerType || (fullCompany as any).customer_type || CustomerType.WebUser,
  partnerName: fullCompany.partnerName || (fullCompany as any).partner_name || '',

  // Business Information



  // Licensing
  licenceStart: fullCompany.licenceStart || (fullCompany as any).licence_start,
  licenceEnd: fullCompany.licenceEnd || (fullCompany as any).licence_end,
  licenceRenewStart: fullCompany.licenceRenewStart || (fullCompany as any).licence_renew_start,
  licenceRenewEnd: fullCompany.licenceRenewEnd || (fullCompany as any).licence_renew_end,
  licenceType: fullCompany.licenceType || (fullCompany as any).licence_type || LicenceType.Basic,
  isDemo: fullCompany.isDemo ?? (fullCompany as any).is_demo ?? false,
  analytics: fullCompany.analytics ?? true,
  calculationsAllotted: fullCompany.calculationsAllotted || (fullCompany as any).calculations_allotted || (fullCompany as any).calculations_alloted || (fullCompany as any).calculationsAllotted || 0,
  calculationsLeft: fullCompany.calculationsLeft || (fullCompany as any).calculations_left,

  // Limits and Counters
  totalNumCalculations: fullCompany.totalNumCalculations || (fullCompany as any).total_num_calculations || 0,
  totalNumGeoLocs: fullCompany.totalNumGeoLocs || (fullCompany as any).total_num_geo_locs || 0,
  geolocationAllotted: fullCompany.geolocationAllotted ?? (fullCompany as any).geolocation_allotted ?? (fullCompany as any).shipmentLimit ?? (fullCompany as any).shipment_limit,
  geolocationLeft: fullCompany.geolocationLeft ?? (fullCompany as any).geolocation_left ?? (fullCompany as any).geoLimit ?? (fullCompany as any).geo_limit,
  addedShipments: fullCompany.addedShipments || (fullCompany as any).added_shipments || 0,
  addedGeoLimit: fullCompany.addedGeoLimit || (fullCompany as any).added_geo_limit || 0,

  // Unique Shipments
  uniqueShipmentsAllotted: fullCompany.uniqueShipmentsAllotted || (fullCompany as any).unique_shipments_allotted || 0,
  uniqueShipmentsLeft: fullCompany.uniqueShipmentsLeft ?? (fullCompany as any).unique_shipments_left,
  totalNumUniqueShipments: fullCompany.totalNumUniqueShipments || (fullCompany as any).total_num_unique_shipments || 0,
  addedUniqueShipments: fullCompany.addedUniqueShipments || (fullCompany as any).added_unique_shipments || 0,

  // Features and Status
  isUnlimited: fullCompany.isUnlimited ?? (fullCompany as any).is_unlimited ?? false,
  certificateAvailable: fullCompany.certificateAvailable ?? (fullCompany as any).certificate_available ?? false,
  disabled: Boolean(fullCompany.disabled),
  resellerId: fullCompany.resellerId || (fullCompany as any).reseller_id,
  showLegBreakdown: fullCompany.showLegBreakdown ?? (fullCompany as any).show_leg_breakdown ?? false,

  // Certificate Text Fields
  upperText: fullCompany.upperText || (fullCompany as any).upper_text || '',
  bottomText: fullCompany.bottomText || (fullCompany as any).bottom_text || '',

  // Storage Keys
  dataStorageKey: fullCompany.dataStorageKey || (fullCompany as any).data_storage_key || '',
});

/** Normalised view of one company row's display fields (camelCase/snake_case tolerant). */
interface CompanyRowView {
  logoUrl?: string;
  licenceType?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  country?: string;
  partnerName?: string;
  analytics: boolean;
  isActive: boolean;
  isDemo: boolean;
}

/** Per-row action visibility flags derived from the current user's role. */
interface CompanyRowPermissions {
  showUsers: boolean;
  showToggleStatus: boolean;
  showDelete: boolean;
  showEdit: boolean;
  showEditReadOnly: boolean;
  showView: boolean;
  showRegenerateApiKey: boolean;
}

/**
 * A licence is valid through the whole calendar day of its end date. The end is
 * stored as midnight at the start of that day, so comparing the raw timestamp to
 * now expired licences at the start of their last day (C4-651).
 */
const isLicenceExpired = (endDate?: Date | string): boolean => {
  if (!endDate) return false;
  const end = new Date(endDate);
  const now = new Date();
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return endDay < today;
};

/** Reads a company row's display fields, tolerating camelCase/snake_case variants. */
const buildCompanyRowView = (company: any): CompanyRowView => {
  const endDate = company.licenceEnd || company.licence_end;
  return {
    logoUrl: company.logoUrl || company.logo_url,
    licenceType: company.licenceType || company.licence_type,
    startDate: company.licenceStart || company.licence_start,
    endDate,
    country: company.addressCountry || company.address_country,
    partnerName: company.partnerName || company.partner_name,
    analytics: company.analytics ?? false,
    isActive: !(company.disabled ?? false) && !isLicenceExpired(endDate),
    isDemo: company.isDemo ?? company.is_demo ?? false,
  };
};

/** Pure derivation of which row action buttons are visible for the current user. */
const computeRowPermissions = (
  accountType: number | undefined,
  companyId: number,
  currentUserCompanyId: number | undefined,
  isBasicLicenceUser: boolean,
): CompanyRowPermissions => {
  const isMasterAdmin = accountType === AccountType.MasterAdmin;
  const isCarbonCareAdmin = accountType === AccountType.CarbonCareAdmin;
  const isUser = accountType === AccountType.User;
  const isSubCompanyAdmin = accountType === AccountType.SubCompanyAdmin;
  const isOwnLicensee = companyId === currentUserCompanyId;
  return {
    showUsers: !!(accountType && accountType >= AccountType.LicenseeAdmin),
    showToggleStatus: isMasterAdmin,
    showDelete: isMasterAdmin,
    // CarbonCare admin can edit its own licensee (like a Licensee Admin) but
    // only has view access to other licensees.
    showEdit: !isUser && !isSubCompanyAdmin && (!isCarbonCareAdmin || isOwnLicensee),
    // A plain User may open the Edit dialog but only in read-only mode.
    showEditReadOnly: isUser,
    showView: !isUser && isCarbonCareAdmin && !isOwnLicensee,
    showRegenerateApiKey: !isBasicLicenceUser && !isCarbonCareAdmin && !isUser && !isSubCompanyAdmin,
  };
};

/** Validation adornment for the company-name field (avoids a nested ternary in JSX). */
const renderNameValidationAdornment = (hasError: boolean, hasValue: boolean): React.ReactNode => {
  if (hasError) return <Cancel sx={{ color: '#f44336' }} />;
  if (hasValue) return <CheckCircle sx={{ color: '#4CAF50' }} />;
  return null;
};

/** Required + min-length check for a certificate text field. Returns the error message, or undefined when valid. */
const certificateTextError = (
  value: string | undefined,
  required: string,
  minLength: number,
  tooShort: string,
): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return required;
  if (trimmed.length < minLength) return tooShort;
  return undefined;
};

/** License end-date error: required when missing, ordering when present but not after start. */
const licenceEndError = (start?: Date | string, end?: Date | string): string | undefined => {
  if (!end) return 'License end date is required';
  if (start && new Date(start) >= new Date(end)) {
    return 'License end date must be after license start date';
  }
  return undefined;
};

/** Real-time error for a required field: message when blank, otherwise null (clear). */
const requiredFieldError = (value: string, message: string): string | null =>
  value.trim() ? null : message;

/**
 * Parses a comma-stripped allotment input. Returns the non-negative integer
 * value, or null when the input should be rejected (non-numeric / negative).
 * Empty string parses to 0 (accepted), matching the original inline guard.
 */
const parseAllottedInput = (raw: string): number | null => {
  if (raw === '' || (!Number.isNaN(Number(raw)) && Number(raw) >= 0)) {
    return Number.parseInt(raw.replaceAll(',', '')) || 0;
  }
  return null;
};

/** Date-only ordering check: error when both present and start is not before end. */
const dateOrderError = (start?: Date, end?: Date): string | undefined =>
  start && end && start >= end ? 'License end date must be after license start date' : undefined;

/** The mirrored end lands a day after the start — dateOrderError rejects an equal end. */
const nextDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);

/**
 * Computes the updated validationErrors map after a licence date change.
 * `which` selects which field was edited; the order error is applied to
 * licenceEnd in both cases (matching the original inline handlers).
 */
const applyLicenceDateErrors = (
  prev: Record<string, string>,
  which: 'start' | 'end',
  newDate: Date | undefined,
  orderError: string | undefined,
): Record<string, string> => {
  const updated = { ...prev };
  if (which === 'start') {
    if (newDate) delete updated.licenceStart;
    else updated.licenceStart = 'License start date is required';
    if (orderError) updated.licenceEnd = orderError;
    else delete updated.licenceEnd;
  } else if (!newDate) {
    updated.licenceEnd = 'License end date is required';
  } else if (orderError) {
    updated.licenceEnd = orderError;
  } else {
    delete updated.licenceEnd;
  }
  return updated;
};

/** Real-time error for a min-length text field: required, then min-length, else null (clear). */
const minLengthFieldError = (
  value: string,
  required: string,
  minLength: number,
  tooShort: string,
): string | null => {
  if (!value.trim()) return required;
  if (value.trim().length < minLength) return tooShort;
  return null;
};

/**
 * Pure per-field validator for the live (on-change/on-blur) validation.
 * Returns a string to set the error, or null to clear it. Returns undefined
 * for fields with no live validation so the caller leaves the map untouched.
 */
const computeFieldError = (field: string, value: string): string | null | undefined => {
  switch (field) {
    case 'name':
      return requiredFieldError(value, 'Licensee name is required');
    case 'addressPostalCode':
      return requiredFieldError(value, 'Postal code is required');
    case 'addressCountry':
      return requiredFieldError(value, 'Country is required');
    case 'addressCity':
      return requiredFieldError(value, 'City is required');
    case 'addressState':
      return null;
    case 'upperText':
      return minLengthFieldError(value, 'Upper text is required', 10, 'Upper text should be at least 10 characters');
    case 'bottomText':
      return minLengthFieldError(value, 'Bottom text is required', 5, 'Bottom text should be at least 5 characters');
    default:
      return undefined;
  }
};

/** Stable keys for the fixed set of loading skeleton rows. */
const COMPANY_SKELETON_ROW_KEYS = Array.from({ length: 10 }, (_, i) => `company-skeleton-${i}`);

/** Loading-state skeleton rows for the companies table. */
const CompanyTableSkeletonRows: React.FC = () => (
  <>
    {COMPANY_SKELETON_ROW_KEYS.map((rowKey, idx) => (
      <TableRow key={rowKey}>
        <TableCell><Skeleton variant="text" width={32} /></TableCell>
        <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
        <TableCell><Skeleton variant="text" width={`${50 + ((idx * 9) % 30)}%`} /></TableCell>
        <TableCell><Skeleton variant="text" width={80} /></TableCell>
        <TableCell><Skeleton variant="text" width={90} /></TableCell>
        <TableCell><Skeleton variant="text" width={90} /></TableCell>
        <TableCell><Skeleton variant="text" width={70} /></TableCell>
        <TableCell><Skeleton variant="text" width={80} /></TableCell>
        <TableCell><Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: '12px' }} />
          </Box>
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="circular" width={28} height={28} />
          </Box>
        </TableCell>
      </TableRow>
    ))}
  </>
);

interface CompanyTableRowProps {
  company: any;
  permissions: CompanyRowPermissions;
  onManageUsers: (company: Company) => void;
  onToggleStatus: (company: Company) => void;
  onDelete: (company: Company) => void;
  onEdit: (company: Company) => void;
  onView: (company: Company) => void;
  onRegenerateApiKey: (company: Company) => void;
}

/** A single company row, including the role-gated action buttons. */
const CompanyTableRow: React.FC<CompanyTableRowProps> = ({
  company,
  permissions,
  onManageUsers,
  onToggleStatus,
  onDelete,
  onEdit,
  onView,
  onRegenerateApiKey,
}) => {
  const view = buildCompanyRowView(company);
  const { logoUrl, licenceType, startDate, endDate, country, partnerName, analytics, isActive, isDemo } = view;
  const {
    showUsers,
    showToggleStatus,
    showDelete,
    showEdit,
    showEditReadOnly,
    showView,
    showRegenerateApiKey,
  } = permissions;

  return (
    <TableRow key={company.id} hover>
      <TableCell>{company.id}</TableCell>
      <TableCell>
        {logoUrl ? (
          <Avatar src={logoUrl} alt="Licensee Logo" sx={{ width: 32, height: 32 }} />
        ) : (
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#8BC34A' }}>
            <Business />
          </Avatar>
        )}
      </TableCell>
      <TableCell>{company.name}</TableCell>
      <TableCell>{licenceType === 'AsTag' ? 'ASTAG' : licenceType}</TableCell>
      <TableCell>{startDate ? new Date(startDate).toLocaleDateString() : 'N/A'}</TableCell>
      <TableCell>{endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}</TableCell>
      <TableCell>{country || 'N/A'}</TableCell>
      <TableCell>{partnerName || 'N/A'}</TableCell>
      <TableCell>
        <Chip
          label={analytics ? 'Enabled' : 'Disabled'}
          color={analytics ? 'success' : 'default'}
          size="small"
          variant={analytics ? 'filled' : 'outlined'}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'default'}
            size="small"
          />
          {isDemo && (
            <Chip
              label="DEMO"
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: 0.4,
                borderColor: 'warning.main',
                color: 'warning.dark',
                backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.1),
              }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        {showUsers && (
          <Tooltip title="Manage users" arrow>
            <IconButton size="small" onClick={() => onManageUsers(company)}>
              <Person fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {showToggleStatus && (
          <Tooltip title={isActive ? 'Deactivate company' : 'Activate company'} arrow>
            <IconButton
              size="small"
              onClick={() => onToggleStatus(company)}
              sx={{ color: isActive ? 'success.main' : 'error.main' }}
            >
              {isActive ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {showDelete && (
          <Tooltip title="Delete Licensee" arrow>
            <IconButton
              size="small"
              onClick={() => onDelete(company)}
              sx={{ color: 'error.main' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {(showEdit || showEditReadOnly) && (
          <Tooltip title="Edit company" arrow>
            <IconButton
              size="small"
              onClick={() => (showEditReadOnly ? onView(company) : onEdit(company))}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {showView && (
          <Tooltip title="View company" arrow>
            <IconButton size="small" onClick={() => onView(company)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {showRegenerateApiKey && (
          <Tooltip title="Regenerate API key" arrow>
            <IconButton size="small" onClick={() => onRegenerateApiKey(company)}>
              <KeyOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

interface CompaniesTableBodyProps extends Omit<CompanyTableRowProps, 'company' | 'permissions'> {
  loading: boolean;
  companies: any[];
  accountType: number | undefined;
  currentUserCompanyId: number | undefined;
  isBasicLicenceUser: boolean;
}

/** Table body: loading skeletons, empty state, or one row per company. */
const CompaniesTableBody: React.FC<CompaniesTableBodyProps> = ({
  loading,
  companies,
  accountType,
  currentUserCompanyId,
  isBasicLicenceUser,
  ...rowHandlers
}) => {
  if (loading) {
    return <CompanyTableSkeletonRows />;
  }
  if (companies.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
          <Typography color="text.secondary">No licensees found</Typography>
        </TableCell>
      </TableRow>
    );
  }
  return (
    <>
      {companies.map((company: any) => (
        <CompanyTableRow
          key={company.id}
          company={company}
          permissions={computeRowPermissions(accountType, company.id, currentUserCompanyId, isBasicLicenceUser)}
          {...rowHandlers}
        />
      ))}
    </>
  );
};

/** Title for the company dialog: edit / view / create. */
const companyDialogTitle = (
  editingCompany: Company | null,
  viewCompany: Company | null,
): string => {
  if (editingCompany) return `Edit Company - ${editingCompany.name}`;
  if (viewCompany) return `View Company - ${viewCompany.name}`;
  return 'Create New Licensee';
};

interface CompanyDialogActionsProps {
  saving: boolean;
  viewCompany: Company | null;
  editingCompany: Company | null;
  submitDisabled: boolean;
  formComplete: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

/** Footer actions for the company dialog: Close (view) or Cancel + Save (create/edit). */
const CompanyDialogActions: React.FC<CompanyDialogActionsProps> = ({
  saving,
  viewCompany,
  editingCompany,
  submitDisabled,
  formComplete,
  onClose,
  onSubmit,
}) => {
  const saveButtonLabel = editingCompany ? 'Update Licensee' : 'Create Licensee';
  return (
  <DialogActions sx={{
    p: 4,
    borderTop: '1px solid',
    borderColor: 'divider',
    background: '#f8f9fa',
    gap: 2,
    justifyContent: 'space-between'
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {saving && <LinearProgress sx={{ width: 200 }} />}
    </Box>
    {viewCompany ? (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          size="large"
          sx={{ px: 4, borderRadius: 2, fontWeight: 600 }}
        >
          Close
        </Button>
      </Box>
    ) : (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          size="large"
          sx={{ px: 4, borderRadius: 2, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          startIcon={saving ? null : <Save />}
          // Truly blocked only while saving or when the editor lacks edit rights.
          // Missing mandatory fields keep the button clickable (but styled as
          // disabled) so a click can surface the info toast below.
          disabled={submitDisabled}
          size="large"
          sx={{
            px: 4,
            borderRadius: 2,
            fontWeight: 600,
            ...(formComplete
              ? {
                  background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                  boxShadow: '0 4px 20px 0 rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)',
                    boxShadow: '0 6px 25px 0 rgba(25, 118, 210, 0.4)',
                  },
                }
              : {
                  // looks disabled, stays clickable to trigger the info toast
                  background: '#e0e0e0',
                  color: '#9e9e9e',
                  boxShadow: 'none',
                  '&:hover': { background: '#e0e0e0', boxShadow: 'none' },
                }),
            '&:disabled': {
              background: '#e0e0e0',
              color: '#9e9e9e',
              boxShadow: 'none',
            }
          }}
        >
          {saving ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress sx={{ width: 20, height: 2 }} />
              Saving...
            </Box>
          ) : saveButtonLabel}
        </Button>
      </Box>
    )}
  </DialogActions>
  );
};

interface TransactionAdjusterProps {
  label: string;
  icon: React.ReactNode;
  added: number;
  adjust: number;
  setAdjust: (value: number) => void;
  disabled: boolean;
  borderColor: string;
  background: string;
  onAdd: () => void;
  onSubtract: () => void;
}

interface CompaniesHeaderActionsProps {
  accountType: number | undefined;
  licenceStats: LicenceStatsResponse | null;
  selectedLicenceType: string | null;
  statusFilter: CompanyStatusFilter | null;
  setStatusFilter: (value: CompanyStatusFilter | null) => void;
  currentStatusLabel: string;
  totalRows: number;
  exporting: boolean;
  onTileClick: (licenceType: string | null) => void;
  onExport: (licenceType: string | null) => void;
  onCreate: () => void;
}

/** Page-header action row: status filter, Total card, and Add Licensee button. */
const CompaniesHeaderActions: React.FC<CompaniesHeaderActionsProps> = ({
  accountType,
  licenceStats,
  selectedLicenceType,
  statusFilter,
  setStatusFilter,
  currentStatusLabel,
  totalRows,
  exporting,
  onTileClick,
  onExport,
  onCreate,
}) => (
  <>
    {/* Status Filter - pill-button + popover menu, placed left of the Total card */}
    {accountType !== undefined && accountType >= AccountType.CarbonCareAdmin && (
      <StatusFilterDropdown
        value={statusFilter}
        onChange={setStatusFilter}
        buttonHeight={48}
        buttonGap={1}
        menuItemPy={1.1}
      />
    )}
    {/* Total Card - inline with header */}
    {licenceStats && (
      <Card
        sx={{
          border: '2px solid',
          borderColor: selectedLicenceType === null ? 'primary.main' : 'primary.light',
          background: (theme) => selectedLicenceType === null
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
          transition: 'all 0.2s',
          position: 'relative',
          minWidth: 200,
          borderRadius: 1,
          height: 48,
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%', px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {statusFilter ? currentStatusLabel : 'Total'}
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {totalRows}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title={selectedLicenceType === null ? 'Clear filter' : 'Show all licensees'} arrow>
              <IconButton
                size="small"
                onClick={() => onTileClick(null)}
                sx={{
                  color: selectedLicenceType === null ? 'primary.main' : 'text.secondary',
                  '&:hover': { color: 'primary.main', backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12) },
                }}
              >
                <FilterList fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={exporting ? "Exporting..." : "Export all licensees"} arrow>
              <IconButton
                size="small"
                disabled={exporting || totalRows === 0}
                onClick={() => onExport(null)}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12) },
                }}
              >
                {exporting ? <CircularProgress size={18} /> : <FileDownload fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    )}
    {accountType! > AccountType.CarbonCareAdmin && (
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={onCreate}
        size="large"
      >
        Add Licensee
      </Button>
    )}
  </>
);

interface LicenceStatsCardsProps {
  licenceStats: LicenceStatsResponse | null;
  selectedLicenceType: string | null;
  exporting: boolean;
  onTileClick: (licenceType: string | null) => void;
  onExport: (licenceType: string | null) => void;
}

/** Per-licence-type summary tiles with filter + export controls. */
const LicenceStatsCards: React.FC<LicenceStatsCardsProps> = ({
  licenceStats,
  selectedLicenceType,
  exporting,
  onTileClick,
  onExport,
}) => {
  if (!licenceStats) return null;
  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={2}>
        {licenceStats.byLicenceType.map((stat) => {
          const isSelected = selectedLicenceType === stat.licenceType;
          return (
            <Grid item xs={6} sm={4} md={3} key={stat.licenceType}>
              <Card
                sx={{
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'grey.200',
                  background: isSelected
                    ? (theme: any) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.16)} 100%)`
                    : 'transparent',
                  transition: 'all 0.2s',
                  position: 'relative',
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 4, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Tooltip title={isSelected ? 'Clear filter' : `Filter by ${stat.licenceType.replace('_', ' ')}`} arrow>
                    <IconButton
                      size="small"
                      onClick={() => onTileClick(stat.licenceType)}
                      sx={{
                        color: isSelected ? 'primary.main' : 'text.secondary',
                        '&:hover': { color: 'primary.main', backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) },
                      }}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={exporting ? "Exporting..." : `Export ${stat.licenceType.replace('_', ' ')} licensees`} arrow>
                    <IconButton
                      size="small"
                      disabled={exporting || stat.count === 0}
                      onClick={() => onExport(stat.licenceType)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main', backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) },
                      }}
                    >
                      {exporting ? <CircularProgress size={18} /> : <FileDownload fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color={isSelected ? 'primary.main' : 'text.secondary'} fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {stat.licenceType.replace('_', ' ')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <Typography variant="h5" fontWeight={700} color={isSelected ? 'primary.main' : 'text.primary'}>
                      {stat.count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

/** One allotment-adjuster card (Calculations / Geo / Unique Shipments) with +/- controls. */
const TransactionAdjuster: React.FC<TransactionAdjusterProps> = ({
  label,
  icon,
  added,
  adjust,
  setAdjust,
  disabled,
  borderColor,
  background,
  onAdd,
  onSubtract,
}) => (
  <Grid item xs={12} md={4}>
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor, background }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        {label}: {added}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          type="number"
          placeholder="Amount"
          value={adjust || ''}
          onChange={(e) => setAdjust(e.target.value === '' ? 0 : Number.parseInt(e.target.value) || 0)}
          sx={{
            flex: 1,
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
              '-webkit-appearance': 'none',
              margin: 0,
            },
            '& input[type=number]': {
              '-moz-appearance': 'textfield',
            },
            '& .MuiInputBase-root': {
              height: 40,
            }
          }}
        />
        <Button
          variant="contained"
          size="small"
          color="success"
          onClick={onAdd}
          disabled={disabled || adjust <= 0}
          sx={{ height: 40, minWidth: 48, px: 1, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1 }}
        >
          +
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onSubtract}
          disabled={disabled || adjust <= 0}
          sx={{ height: 40, minWidth: 48, px: 1, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
        >
          -
        </Button>
      </Box>
    </Box>
  </Grid>
);

interface ToggleStatusDialogProps {
  open: boolean;
  company: Company | null;
  toggling: boolean;
  actionLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Confirmation dialog for activating / deactivating a company. */
const ToggleStatusDialog: React.FC<ToggleStatusDialogProps> = ({
  open,
  company,
  toggling,
  actionLabel,
  onCancel,
  onConfirm,
}) => (
  <Dialog
    open={open}
    onClose={toggling ? undefined : onCancel}
    maxWidth="xs"
    fullWidth
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Typography variant="h6" fontWeight="700">
        {company?.disabled ? 'Activate Licensee' : 'Deactivate Licensee'}
      </Typography>
    </DialogTitle>
    <DialogContent>
      <Box sx={{ mt: 1 }}>
        <Typography variant="body1">
          Are you sure you want to <strong>{company?.disabled ? 'activate' : 'deactivate'}</strong>{' '}
          {company?.name}?
        </Typography>
      </Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
      <Button onClick={onCancel} variant="outlined" disabled={toggling}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        color={company?.disabled ? 'success' : 'error'}
        disabled={toggling}
        startIcon={company?.disabled ? <ToggleOn /> : <ToggleOff />}
      >
        {toggling ? 'Updating...' : `Yes, ${actionLabel}`}
      </Button>
    </DialogActions>
  </Dialog>
);

/** Full create/edit form body for the company dialog (extracted to keep the
 *  page component's cognitive complexity low). Props mirror the page state
 *  and handlers; the JSX is unchanged. */
const CompanyFormContent: React.FC<any> = ({
  formData,
  setFormData,
  validationErrors,
  validateField,
  fieldsDisabled,
  editingCompany,
  viewCompany,
  isHigherAdmin,
  isLicenseeAdmin,
  isMasterAdmin,
  showApiKeyInCompanyDialog,
  logoFile,
  handleLogoFileChange,
  countries,
  countryAliasMap,
  formatNumber,
  formatDateTimeLocal,
  renderRemainingField,
  handleLicenceStartChange,
  handleLicenceEndChange,
  handleCalcAllottedChange,
  handleGeoAllottedChange,
  handleUniqueAllottedChange,
  calcAdjust,
  setCalcAdjust,
  geoAdjust,
  setGeoAdjust,
  uniqueShipmentAdjust,
  setUniqueShipmentAdjust,
  theme,
  handleAddCalculations,
  handleSubtractCalculations,
  handleAddGeolocations,
  handleSubtractGeolocations,
  handleAddUniqueShipments,
  handleSubtractUniqueShipments,
}) => (
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>

            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  background: '#ffffff'
                }}
              >
                <Typography variant="h6" component="h2" fontWeight="600" sx={{ color: '#333', mb: 1 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={6} sx={{ mt: 4 }}>
                    <TextField
                      fullWidth
                      disabled={fieldsDisabled}
                      label="Licensee Name"
                      value={formData.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, name: value });
                        validateField('name', value);
                      }}
                      required
                      error={!!validationErrors.name}
                      helperText={validationErrors.name}
                      variant="outlined"
                      InputProps={{
                        endAdornment: renderNameValidationAdornment(!!validationErrors.name, !!formData.name.trim()),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#8BC34A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8BC34A',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          '&.Mui-focused': {
                            color: '#8BC34A',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mb: 1, color: validationErrors.logoFile ? 'error.main' : 'text.secondary', fontSize: '0.875rem' }}>
                      Licensee Logo <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        disabled={!!editingCompany && !isHigherAdmin && !isLicenseeAdmin || !!viewCompany}
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUpload />}
                        sx={{
                          height: 56,
                          borderColor: validationErrors.logoFile ? 'error.main' : '#8BC34A',
                          color: validationErrors.logoFile ? 'error.main' : '#8BC34A',
                          borderRadius: 1,
                          '&:hover': {
                            borderColor: validationErrors.logoFile ? 'error.main' : '#7CB342',
                            backgroundColor: 'rgba(139, 195, 74, 0.04)',
                          }
                        }}
                      >
                        {logoFile ? 'Change Logo' : 'Upload Logo'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleLogoFileChange}
                        />
                      </Button>
                      {(logoFile || formData.logoUrl) && (
                        <Avatar
                          src={logoFile ? URL.createObjectURL(logoFile) : formData.logoUrl}
                          alt="Licensee Logo Preview"
                          sx={{ width: 56, height: 56, border: '2px solid #8BC34A' }}
                        />
                      )}
                    </Box>
                    {logoFile && (
                      <FormHelperText sx={{ color: 'success.main', mt: 1 }}>
                        Selected: {logoFile.name}
                      </FormHelperText>
                    )}
                    {validationErrors.logoFile && (
                      <FormHelperText error>{validationErrors.logoFile}</FormHelperText>
                    )}
                    {!logoFile && !formData.logoUrl && !validationErrors.logoFile && (
                      <FormHelperText>Upload a licensee logo (JPG, PNG, GIF, SVG - max 2MB)</FormHelperText>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Address Information Section */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  background: '#ffffff'
                }}
              >
                <Typography variant="h6" component="h2" fontWeight="600" sx={{ color: '#333', mb: 3 }}>
                  Address & Contact Information
                </Typography>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      disabled={fieldsDisabled}
                      label="Street Address"
                      value={formData.addressStreet}
                      onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
                      placeholder="123 Business Street"
                      helperText="Complete street address with number and street name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#8BC34A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8BC34A',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          '&.Mui-focused': {
                            color: '#8BC34A',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      disabled={fieldsDisabled}
                      label="City"
                      value={formData.addressCity}
                      onChange={(e) => setFormData({ ...formData, addressCity: e.target.value })}
                      onBlur={(e) => validateField('addressCity', e.target.value)}
                      error={!!validationErrors.addressCity}
                      helperText={validationErrors.addressCity}
                      placeholder="City name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#8BC34A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8BC34A',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          '&.Mui-focused': {
                            color: '#8BC34A',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      disabled={fieldsDisabled}
                      label="State/Province"
                      value={formData.addressState}
                      onChange={(e) => setFormData({ ...formData, addressState: e.target.value })}
                      onBlur={(e) => validateField('addressState', e.target.value)}
                      error={!!validationErrors.addressState}
                      helperText={validationErrors.addressState}
                      placeholder="State or Province"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#8BC34A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8BC34A',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          '&.Mui-focused': {
                            color: '#8BC34A',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      disabled={fieldsDisabled}
                      label="Postal Code"
                      required
                      value={formData.addressPostalCode}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, addressPostalCode: value });
                        validateField('addressPostalCode', value);
                      }}
                      error={!!validationErrors.addressPostalCode}
                      helperText={validationErrors.addressPostalCode}
                      placeholder="12345"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#8BC34A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8BC34A',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          '&.Mui-focused': {
                            color: '#8BC34A',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      disabled={fieldsDisabled}
                      options={countries}
                      value={formData.addressCountry || null}
                      onChange={(event, newValue) => {
                        const val = typeof newValue === 'string' ? newValue : '';
                        setFormData({ ...formData, addressCountry: val });
                        validateField('addressCountry', val);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Country"
                          required
                          placeholder="Select or type country name..."
                          error={!!validationErrors.addressCountry}
                          helperText={validationErrors.addressCountry || "Choose from the list or type to search"}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <LocationOn sx={{ mr: 1, color: '#8BC34A' }} />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: 'transparent',
                              '& fieldset': {
                                borderColor: '#e0e0e0',
                              },
                              '&:hover fieldset': {
                                borderColor: '#8BC34A',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#8BC34A',
                                borderWidth: 1,
                              },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.875rem',
                              '&.Mui-focused': {
                                color: '#8BC34A',
                              },
                            },
                          }}
                        />
                      )}
                      filterOptions={(options, { inputValue }) => {
                        const query = inputValue.toLowerCase().trim();
                        if (!query) return options.slice(0, 10);
                        // Match against English name OR any local name in supported languages
                        return options
                          .filter((name: string) => {
                            const aliases = countryAliasMap.get(name);
                            return aliases
                              ? aliases.some((alias: string) => alias.includes(query))
                              : name.toLowerCase().includes(query);
                          })
                          .slice(0, 10);
                      }}
                      freeSolo
                      size="medium"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Configuration Section */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Configuration
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Customer Type</InputLabel>
                      <Select
                        disabled={fieldsDisabled}
                        value={formData.customerType}
                        onChange={(e) => setFormData({ ...formData, customerType: e.target.value as CustomerType })}
                        label="Customer Type"
                      >
                        {Object.values(CustomerType).map(type => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      disabled={fieldsDisabled}
                      label="Partner Name"
                      value={formData.partnerName}
                      onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Licensing Section */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Licensing
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>License Type</InputLabel>
                      <Select
                        disabled={fieldsDisabled}
                        value={formData.licenceType}
                        onChange={(e) => setFormData({ ...formData, licenceType: e.target.value as LicenceType })}
                        label="License Type"
                      >
                        {Object.values(LicenceType).map(type => (
                          <MenuItem key={type} value={type}>{type === 'AsTag' ? 'ASTAG' : type.replace('_', ' ')}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', pt: '0 !important' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={!!editingCompany || !!viewCompany}
                          checked={formData.isDemo}
                          onChange={(e) => setFormData({ ...formData, isDemo: e.target.checked })}
                        />
                      }
                      label="Demo Account"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={fieldsDisabled}
                          checked={formData.analytics}
                          onChange={(e) => setFormData({ ...formData, analytics: e.target.checked })}
                        />
                      }
                      label="Analytics Enabled"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CarbonCareDatePicker
                      fullWidth
                      disabled={fieldsDisabled}
                      label="License Start"
                      required
                      value={formData.licenceStart}
                      onChange={handleLicenceStartChange}
                      error={!!validationErrors.licenceStart}
                      helperText={validationErrors.licenceStart || "Select the license start date"}
                      startAdornment={
                        <InputAdornment position="start">
                          <CalendarToday sx={{ color: '#8BC34A' }} />
                        </InputAdornment>
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#8BC34A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8BC34A',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          '&.Mui-focused': {
                            color: '#8BC34A',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CarbonCareDatePicker
                      fullWidth
                      disabled={fieldsDisabled}
                      label="License End"
                      required
                      value={formData.licenceEnd}
                      onChange={handleLicenceEndChange}
                      error={!!validationErrors.licenceEnd}
                      helperText={validationErrors.licenceEnd || "Select the license expiration date"}
                      startAdornment={
                        <InputAdornment position="start">
                          <CalendarToday sx={{ color: '#8BC34A' }} />
                        </InputAdornment>
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#8BC34A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8BC34A',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          '&.Mui-focused': {
                            color: '#8BC34A',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Limits & Features Section */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Limits & Features
                </Typography>
                <Grid container spacing={3}>
                  {/* 3-column layout: Calculations | Geolocations | Unique Shipments */}
                  {/* Each column has Allotted on top, Left below */}

                  {/* Calculations Column */}
                  <Grid item xs={12} md={4}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          disabled={!!editingCompany || !!viewCompany}
                          label="Calculation Allotted"
                          value={formatNumber(formData.calculationsAllotted)}
                          onChange={(e) => handleCalcAllottedChange(e.target.value.replaceAll(',', ''))}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Numbers sx={{ color: '#8BC34A' }} />
                              </InputAdornment>
                            ),
                          }}
                          helperText="Total calculations allowed"
                          placeholder="0"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: 'transparent',
                              '& fieldset': { borderColor: '#e0e0e0' },
                              '&:hover fieldset': { borderColor: '#8BC34A' },
                              '&.Mui-focused fieldset': { borderColor: '#8BC34A', borderWidth: 1 },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.875rem',
                              '&.Mui-focused': { color: '#8BC34A' },
                            },
                          }}
                        />
                      </Grid>
                      {renderRemainingField('Calculations Left', formData.calculationsLeft, 'Remaining calculations available')}
                    </Grid>
                  </Grid>

                  {/* Geolocations Column */}
                  <Grid item xs={12} md={4}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          disabled={!!editingCompany || !!viewCompany}
                          label="Geolocation Allotted"
                          value={formatNumber(formData.geolocationAllotted)}
                          onChange={(e) => handleGeoAllottedChange(e.target.value.replaceAll(',', ''))}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Numbers sx={{ color: '#8BC34A' }} />
                              </InputAdornment>
                            ),
                          }}
                          helperText={editingCompany || viewCompany ? "Maximum geolocations allowed" : "Defaults to 1% of Calculation Allotted (editable)"}
                          placeholder=""
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: 'transparent',
                              '& fieldset': { borderColor: '#e0e0e0' },
                              '&:hover fieldset': { borderColor: '#8BC34A' },
                              '&.Mui-focused fieldset': { borderColor: '#8BC34A', borderWidth: 1 },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.875rem',
                              '&.Mui-focused': { color: '#8BC34A' },
                            },
                          }}
                        />
                      </Grid>
                      {renderRemainingField('Geolocation Left', formData.geolocationLeft, 'Remaining geolocation lookups')}
                    </Grid>
                  </Grid>

                  {/* Unique Shipments Column */}
                  <Grid item xs={12} md={4}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          disabled={!!editingCompany || !!viewCompany}
                          label="Unique Shipments Allotted"
                          value={formatNumber(formData.uniqueShipmentsAllotted)}
                          onChange={(e) => handleUniqueAllottedChange(e.target.value.replaceAll(',', ''))}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Numbers sx={{ color: '#8BC34A' }} />
                              </InputAdornment>
                            ),
                          }}
                          helperText="Total unique shipments allowed"
                          placeholder="0"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: 'transparent',
                              '& fieldset': { borderColor: '#e0e0e0' },
                              '&:hover fieldset': { borderColor: '#8BC34A' },
                              '&.Mui-focused fieldset': { borderColor: '#8BC34A', borderWidth: 1 },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.875rem',
                              '&.Mui-focused': { color: '#8BC34A' },
                            },
                          }}
                        />
                      </Grid>
                      {renderRemainingField('Unique Shipments Left', formData.uniqueShipmentsLeft, 'Remaining unique shipments')}
                    </Grid>
                  </Grid>

                  {editingCompany && isMasterAdmin && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                        Transactions management
                      </Typography>
                      <Grid container spacing={2}>
                        <TransactionAdjuster
                          label="Calculations Added"
                          icon={<Numbers fontSize="small" color="primary" />}
                          added={(formData.calculationsAllotted || 0) - (editingCompany?.calculationsAllotted ?? editingCompany?.calculations_allotted ?? 0)}
                          adjust={calcAdjust}
                          setAdjust={setCalcAdjust}
                          disabled={fieldsDisabled}
                          borderColor={alpha(theme.palette.primary.main, 0.2)}
                          background={alpha(theme.palette.primary.main, 0.02)}
                          onAdd={handleAddCalculations}
                          onSubtract={handleSubtractCalculations}
                        />
                        <TransactionAdjuster
                          label="Geo Locations Added"
                          icon={<LocationOn fontSize="small" color="info" />}
                          added={(formData.geolocationAllotted || 0) - (editingCompany?.geolocationAllotted ?? editingCompany?.geolocation_allotted ?? 0)}
                          adjust={geoAdjust}
                          setAdjust={setGeoAdjust}
                          disabled={fieldsDisabled}
                          borderColor={alpha(theme.palette.info.main, 0.2)}
                          background={alpha(theme.palette.info.main, 0.02)}
                          onAdd={handleAddGeolocations}
                          onSubtract={handleSubtractGeolocations}
                        />
                        <TransactionAdjuster
                          label="Unique Shipments Added"
                          icon={<Numbers fontSize="small" color="warning" />}
                          added={(formData.uniqueShipmentsAllotted || 0) - (editingCompany?.uniqueShipmentsAllotted ?? editingCompany?.unique_shipments_allotted ?? 0)}
                          adjust={uniqueShipmentAdjust}
                          setAdjust={setUniqueShipmentAdjust}
                          disabled={fieldsDisabled}
                          borderColor={alpha(theme.palette.warning.main, 0.2)}
                          background={alpha(theme.palette.warning.main, 0.02)}
                          onAdd={handleAddUniqueShipments}
                          onSubtract={handleSubtractUniqueShipments}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={fieldsDisabled}
                          checked={formData.isUnlimited}
                          onChange={(e) => setFormData({ ...formData, isUnlimited: e.target.checked })}
                        />
                      }
                      label="Unlimited Account"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={fieldsDisabled}
                          checked={formData.certificateAvailable}
                          onChange={(e) => setFormData({ ...formData, certificateAvailable: e.target.checked })}
                        />
                      }
                      label="Certificate Available"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={fieldsDisabled}
                          checked={formData.disabled}
                          onChange={(e) => setFormData({ ...formData, disabled: e.target.checked })}
                        />
                      }
                      label="Disabled"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={fieldsDisabled}
                          checked={formData.showLegBreakdown}
                          onChange={(e) => setFormData({ ...formData, showLegBreakdown: e.target.checked })}
                        />
                      }
                      label="Show Leg-Wise Breakdown (XML API)"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Certificate & Remarks Section */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Certificate & Remarks
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      disabled={fieldsDisabled}
                      label="Upper Text"
                      required
                      multiline
                      rows={3}
                      value={formData.upperText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, upperText: value });
                        validateField('upperText', value);
                      }}
                      error={!!validationErrors.upperText}
                      helperText={validationErrors.upperText}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      disabled={fieldsDisabled}
                      label="Bottom Text"
                      required
                      multiline
                      rows={3}
                      value={formData.bottomText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, bottomText: value });
                        validateField('bottomText', value);
                      }}
                      error={!!validationErrors.bottomText}
                      helperText={validationErrors.bottomText}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* System Information Section (Only for Edit) */}
            {showApiKeyInCompanyDialog && (
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    System Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="API Key"
                        value={editingCompany.apiKey || editingCompany.api_key || ''}
                        disabled
                        helperText="Use the regenerate button in actions to create a new key"
                        InputProps={{
                          style: { fontFamily: 'monospace' }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

          </Grid>
        </DialogContent>
);


const CompaniesPage: React.FC = () => {
  const theme = useTheme();
  const { user: currentUser } = useAuthStore();
  const isBasicLicence = (licenceType?: string | null) => licenceType?.toLowerCase() === LicenceType.Basic.toLowerCase();

  // Country list + alias map — computed once on mount.
  // The alias map lets users search by any local name (e.g. "Deutschland" → Germany,
  // "Allemagne" → Germany, "España" → Spain, "Россия" → Russia, etc.)
  const { countries, countryAliasMap } = useMemo(() => {
    const allLocales = [
      enLocale, deLocale, frLocale, esLocale, itLocale, ptLocale,
      ruLocale, zhLocale, jaLocale, arLocale, koLocale, nlLocale,
      plLocale, trLocale, svLocale, hiLocale,
    ];
    allLocales.forEach(loc => countriesLib.registerLocale(loc));

    const supportedLangs = ['de', 'fr', 'es', 'it', 'pt', 'ru', 'zh', 'ja', 'ar', 'ko', 'nl', 'pl', 'tr', 'sv', 'hi'];
    const englishEntries = countriesLib.getNames('en', { select: 'official' }); // { 'DE': 'Germany', ... }

    // Build: englishName -> string[] of all lowercased local names across supported languages
    const countryAliasMap = new Map<string, string[]>();
    for (const [code, englishName] of Object.entries(englishEntries)) {
      const aliases: string[] = [englishName.toLowerCase()];
      for (const lang of supportedLangs) {
        const localName = countriesLib.getName(code, lang);
        if (localName) aliases.push(localName.toLowerCase());
      }
      countryAliasMap.set(englishName, aliases);
    }

    const countries = (Object.values(englishEntries)).sort((a, b) =>
      a.localeCompare(b, 'en', { sensitivity: 'base' })
    );

    return { countries, countryAliasMap };
  }, []);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [viewCompany, setViewCompany] = useState<Company | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [userManagerOpen, setUserManagerOpen] = useState(false);
  const [userCompany, setUserCompany] = useState<Company | null>(null);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [geoManuallyEdited, setGeoManuallyEdited] = useState(false);
  const [calcAdjust, setCalcAdjust] = useState<number>(0);
  const [geoAdjust, setGeoAdjust] = useState<number>(0);
  const [uniqueShipmentAdjust, setUniqueShipmentAdjust] = useState<number>(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [companyToToggle, setCompanyToToggle] = useState<Company | null>(null);
  const [toggling, setToggling] = useState(false);
  const [licenceStats, setLicenceStats] = useState<LicenceStatsResponse | null>(null);
  const [selectedLicenceType, setSelectedLicenceType] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CompanyStatusFilter | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [openAddUserForNewCompany, setOpenAddUserForNewCompany] = useState(false);

  // A CarbonCare admin editing its OWN licensee is treated exactly like a Licensee Admin
  // (same editable fields). For any other licensee it only gets view access. Grouped in a
  // helper so this permission logic doesn't inflate the component's cognitive complexity.
  const derivePermissions = () => {
    const acct = currentUser?.accountType ?? 0;
    const isCarbonCareEditingOwnLicensee =
      acct === AccountType.CarbonCareAdmin && !!editingCompany && editingCompany.id === currentUser?.companyId;
    const isLicenseeAdmin = acct === AccountType.LicenseeAdmin || isCarbonCareEditingOwnLicensee;
    // CarbonCare admin is intentionally excluded from "higher admin" so that, when editing
    // its own licensee, it gets the restricted Licensee-Admin field set rather than full edit.
    const isHigherAdmin = acct > AccountType.CarbonCareAdmin;
    const fieldsDisabled = !!editingCompany && !isHigherAdmin || !!viewCompany;
    const isMasterAdmin = acct === AccountType.MasterAdmin;
    const currentUserLicenceType = currentUser?.license_type || null;
    const isBasicLicenceUser = isBasicLicence(currentUserLicenceType) && !isMasterAdmin;
    const showApiKeyInCompanyDialog = !!editingCompany && !isBasicLicenceUser;
    return { isLicenseeAdmin, isHigherAdmin, fieldsDisabled, isMasterAdmin, isBasicLicenceUser, showApiKeyInCompanyDialog };
  };
  const { isLicenseeAdmin, isHigherAdmin, fieldsDisabled, isMasterAdmin, isBasicLicenceUser, showApiKeyInCompanyDialog } = derivePermissions();

  const statusOptions: Array<{ value: CompanyStatusFilter | null; label: string; dotColor: string }> = useMemo(() => [
    { value: null, label: 'All Statuses', dotColor: theme.palette.grey[400] },
    { value: 'active', label: 'Active', dotColor: theme.palette.success.main },
    { value: 'inactive', label: 'Inactive', dotColor: theme.palette.error.main },
    { value: 'demo', label: 'Demo', dotColor: theme.palette.warning.main },
  ], [theme]);

  const currentStatus = statusOptions.find((o) => o.value === statusFilter) ?? statusOptions[0];

  // Changing the status filter clears any active search so the two filters
  // don't compound unexpectedly. Clear both the input and its debounced value
  // so the next fetch (triggered by the statusFilter change) uses an empty query.
  const handleStatusFilterChange = (value: CompanyStatusFilter | null) => {
    setStatusFilter(value);
    setSearchTerm('');
    setDebouncedSearch('');
  };

  // Format number with international commas (e.g. 1,111,222)
  const formatNumber = (val: number | undefined | null): string => {
    if (val === undefined || val === null) return '';
    return val.toLocaleString('en-US');
  };

  // Read-only "...Left" field shown only when editing/viewing an existing company.
  // Shared by the Calculations / Geolocation / Unique Shipments "Left" fields.
  const renderRemainingField = (
    label: string,
    value: number | undefined | null,
    helperText: string,
  ) => (editingCompany || viewCompany) && (
    <Grid item xs={12}>
      <TextField
        fullWidth
        disabled={true}
        label={label}
        value={value == null ? '' : formatNumber(value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Numbers sx={{ color: '#8BC34A' }} />
            </InputAdornment>
          ),
        }}
        helperText={helperText}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            backgroundColor: 'transparent',
            '& fieldset': { borderColor: '#e0e0e0' },
            '&:hover fieldset': { borderColor: '#8BC34A' },
            '&.Mui-focused fieldset': { borderColor: '#8BC34A', borderWidth: 1 },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            '&.Mui-focused': { color: '#8BC34A' },
          },
        }}
      />
    </Grid>
  );

  // Helper function to safely format date for date input (date only, no time)
  const formatDateTimeLocal = (date: Date | string | undefined): string => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (Number.isNaN(dateObj.getTime())) return '';

      const pad = (num: number) => num.toString().padStart(2, '0');
      const year = dateObj.getFullYear();
      const month = pad(dateObj.getMonth() + 1);
      const day = pad(dateObj.getDate());

      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  const [formData, setFormData] = useState<CreateCompanyData>(createInitialFormData);

  // Fetch companies from backend whenever pagination or filters change
  useEffect(() => {
    loadCompanies();
  }, [page, rowsPerPage, sortBy, sortDir, debouncedSearch, selectedLicenceType, statusFilter]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, selectedLicenceType, statusFilter]);


  const loadLicenceStats = async () => {
    if (currentUser?.accountType && currentUser.accountType >= AccountType.CarbonCareAdmin) {
      try {
        const stats = await companyApi.getLicenceStats(statusFilter ?? undefined);
        setLicenceStats(stats);
      } catch (err) {
        console.error('Failed to load licence stats:', err);
      }
    }
  };

  useEffect(() => {
    loadLicenceStats();
  }, [currentUser?.accountType, statusFilter]);

  const handleTileClick = (licenceType: string | null) => {
    setSelectedLicenceType(prev => prev === licenceType ? null : licenceType);
  };

  const handleExportLicenceType = async (licenceType: string | null) => {
    try {
      setExporting(true);
      const blob = await companyApi.exportExcel(licenceType || undefined, statusFilter ?? undefined);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Licensees_${licenceType || 'All'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export licensees');
    } finally {
      setExporting(false);
    }
  };

  const loadCompanies = async () => {
    try {
      setLoading(true);

      // Map camelCase UI field names to snake_case API param names
      const fieldMap: Record<string, string> = {
        name: 'name',
        licenceType: 'licence_type',
        licenceStart: 'licence_start',
        licenceEnd: 'licence_end',
        userCount: 'user_count',
        country: 'address_country',
        partner: 'partner_name',
        analytics: 'analytics',
      };
      const sortByApi = fieldMap[sortBy] ?? sortBy;

      const result = await companyApi.getAll({
        page: page + 1, // 0-indexed UI, 1-indexed API
        limit: rowsPerPage,
        query: searchTerm,
        sortBy: sortByApi,
        sortOrder: sortDir,
        ...(selectedLicenceType ? { licenceType: selectedLicenceType } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        fields: 'id,name,logo_url,licence_type,licence_start,licence_end,disabled,is_demo,address_country,partner_name,analytics',
      });
      setCompanies(result.data);
      setFilteredCompanies(result.data);
      setTotalRows(result.total);
    } catch (error: any) {
      setError('Failed to load companies');
      console.error('Load companies error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(createInitialFormData());
    setLogoFile(null);
    setValidationErrors({});
  };

  // Real-time validation for individual fields. Delegates the per-field rule
  // to the pure computeFieldError helper: string => set, null => clear,
  // undefined => leave untouched.
  const validateField = (field: string, value: string) => {
    const result = computeFieldError(field, value);
    if (result === undefined) return;
    const errors = { ...validationErrors };
    if (result === null) {
      delete errors[field];
    } else {
      errors[field] = result;
    }
    setValidationErrors(errors);
  };

  // Pure presence-check for every mandatory (*) field — drives the submit
  // button's enabled state. validateForm() (below) additionally enforces
  // format/length rules and surfaces inline errors on submit.
  const isFormComplete = (): boolean =>
    !!formData.name.trim() &&
    (!!logoFile || !!formData.logoUrl) &&
    !!formData.addressPostalCode?.trim() &&
    !!formData.addressCountry?.trim() &&
    !!formData.addressCity?.trim() &&
    !!formData.licenceStart &&
    !!formData.licenceEnd &&
    !!formData.upperText?.trim() &&
    !!formData.bottomText?.trim();

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      errors.name = 'Company name is required';
    }

    if (!logoFile && !formData.logoUrl) {
      errors.logoFile = 'Licensee logo is required';
    }

    if (!formData.addressPostalCode?.trim()) {
      errors.addressPostalCode = 'Postal code is required';
    }

    if (!formData.addressCountry?.trim()) {
      errors.addressCountry = 'Country is required';
    }

    if (!formData.addressCity?.trim()) {
      errors.addressCity = 'City is required';
    }

    if (!formData.licenceStart) {
      errors.licenceStart = 'License start date is required';
    }

    const endError = licenceEndError(formData.licenceStart, formData.licenceEnd);
    if (endError) {
      errors.licenceEnd = endError;
    }

    // Certificate text validation
    const upperError = certificateTextError(
      formData.upperText,
      'Upper text is required',
      10,
      'Upper text should be at least 10 characters',
    );
    if (upperError) {
      errors.upperText = upperError;
    }

    const bottomError = certificateTextError(
      formData.bottomText,
      'Bottom text is required',
      5,
      'Bottom text should be at least 5 characters',
    );
    if (bottomError) {
      errors.bottomText = bottomError;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLicenceStartChange = (dateValue: string) => {
    const newDate = (dateValue ? parseIsoDate(dateValue) : null) ?? undefined;
    // Mirror the start into an empty end only; an end that already has a value
    // stays put. The mirror lands on the following day because the end has to be
    // *after* the start, and mirroring the date itself trips that validation.
    const mirroredEnd = newDate && !formData.licenceEnd ? nextDay(newDate) : undefined;
    const effectiveEnd = mirroredEnd ?? formData.licenceEnd;
    const orderError = dateOrderError(newDate, effectiveEnd);
    setFormData(prev => ({
      ...prev,
      licenceStart: newDate,
      ...(mirroredEnd ? { licenceEnd: mirroredEnd } : {}),
    }));
    setValidationErrors(prev => applyLicenceDateErrors(prev, 'start', newDate, orderError));
  };

  const handleLicenceEndChange = (dateValue: string) => {
    const newDate = (dateValue ? parseIsoDate(dateValue) : null) ?? undefined;
    const orderError = dateOrderError(formData.licenceStart, newDate);
    setFormData(prev => ({ ...prev, licenceEnd: newDate }));
    setValidationErrors(prev => applyLicenceDateErrors(prev, 'end', newDate, orderError));
  };

  // Allotment-field change handlers. parseAllottedInput rejects invalid input
  // (returns null) so the handler simply bails out.
  const handleCalcAllottedChange = (rawValue: string) => {
    const newCalc = parseAllottedInput(rawValue);
    if (newCalc === null) return;
    const keepGeo = !!editingCompany || !!viewCompany || geoManuallyEdited;
    setFormData(prev => ({
      ...prev,
      calculationsAllotted: newCalc,
      // For new licensees, geolocation defaults to 1% of calculations
      // unless the user has manually edited it.
      ...(keepGeo ? {} : { geolocationAllotted: Math.round(newCalc / 100) }),
    }));
  };
  const handleGeoAllottedChange = (rawValue: string) => {
    const value = parseAllottedInput(rawValue);
    if (value === null) return;
    setGeoManuallyEdited(true);
    setFormData(prev => ({ ...prev, geolocationAllotted: value }));
  };
  const handleUniqueAllottedChange = (rawValue: string) => {
    const value = parseAllottedInput(rawValue);
    if (value === null) return;
    setFormData(prev => ({ ...prev, uniqueShipmentsAllotted: value }));
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        setValidationErrors(prev => ({
          ...prev,
          logoFile: 'Please select a valid image file (JPG, PNG, GIF, SVG)'
        }));
        return;
      }

      // Validate file size (max 2MB — matches the backend upload limit)
      if (file.size > 2 * 1024 * 1024) {
        setValidationErrors(prev => ({
          ...prev,
          logoFile: 'File size must be less than 2MB'
        }));
        return;
      }

      setLogoFile(file);
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.logoFile;
        return newErrors;
      });
    }
  };

  const handleCreateClick = () => {
    setEditingCompany(null);
    setGeoManuallyEdited(false);
    resetForm();
    setCalcAdjust(0);
    setGeoAdjust(0);
    setUniqueShipmentAdjust(0);
    setOpenDialog(true);
  };

  const handleEditClick = async (company: Company) => {
    try {
      const fullCompany = await companyApi.getById(company.id);
      setEditingCompany(fullCompany);
      setFormData(mapCompanyToFormData(fullCompany));
      setLogoFile(null);
      setValidationErrors({});
      setCalcAdjust(0);
      setGeoAdjust(0);
      setUniqueShipmentAdjust(0);
      setOpenDialog(true);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load company details');
    }
  };
  const handleViewClick = async (company: Company) => {
    try {
      const fullCompany = await companyApi.getById(company.id);
      setViewCompany(fullCompany);
      setFormData(mapCompanyToFormData(fullCompany));
      setLogoFile(null);
      setValidationErrors({});
      setCalcAdjust(0);
      setGeoAdjust(0);
      setUniqueShipmentAdjust(0);
      setOpenDialog(true);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load company details');
    }
  };

  const handleSubmit = async () => {
    // Validate form before submission
    if (!validateForm()) {
      setInfo('Please fill in all mandatory fields marked with *');
      return;
    }

    try {
      setSaving(true);
      console.log('Saving company data:', formData);
      console.log('Logo file:', logoFile);

      // Handle logo file upload if present
      let submitData = { ...formData };
      if (logoFile) {
        const logoUrl = await companyApi.uploadLogo(logoFile);
        submitData.logoUrl = logoUrl;
      }

      // Remove dataStorageKey from updates - it should not be editable
      // Also strip the lifetime "total_num_*" counters — the dashboard no longer
      // mutates them; the +/- controls only change the current Allotted/Left.
      if (editingCompany) {
        const {
          dataStorageKey,
          totalNumCalculations,
          totalNumGeoLocs,
          totalNumUniqueShipments,
          ...updateData
        } = submitData;
        const updatedCompany = await companyApi.update(editingCompany.id, updateData);
        setCompanies(prev => prev.map(c => c.id === editingCompany.id ? updatedCompany : c));
        setFilteredCompanies(prev => prev.map(c => c.id === editingCompany.id ? updatedCompany : c));
        setSuccess('Licensee updated successfully');
        loadLicenceStats();
      } else {
        const newCompany = await companyApi.create(submitData);
        setSuccess(`Company created successfully. API Key: ${newCompany.apiKey || 'Generated'}`);
        console.log('Company created:', newCompany);
        await loadCompanies();
        loadLicenceStats();

        // Open user manager for the new company to add a Licensee Admin
        setUserCompany(newCompany);
        setUserManagerOpen(true);
        setOpenAddUserForNewCompany(true);
      }
      setOpenDialog(false);
      setLogoFile(null);
      setValidationErrors({}); // Clear validation errors on successful save
    } catch (error: any) {
      console.error('Save error:', error);
      let errorMessage = 'Failed to save company';

      if (error.response?.data?.error) {
        // Backend validation error
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        // Backend general error
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Network or other error
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = (company: Company) => {
    setCompanyToToggle(company);
    setToggleConfirmOpen(true);
  };

  const handleToggleConfirm = async () => {
    if (!companyToToggle) return;
    try {
      setToggling(true);
      const isCurrentlyActive = !(companyToToggle.disabled ?? false);
      const updatedCompany = isCurrentlyActive
        ? await companyApi.deactivate(companyToToggle.id)
        : await companyApi.activate(companyToToggle.id);

      // Update both companies and filteredCompanies
      const updatedCompanies = companies.map(c => c.id === companyToToggle.id ? updatedCompany : c);
      setCompanies(updatedCompanies);
      setFilteredCompanies(filteredCompanies.map(c => c.id === companyToToggle.id ? updatedCompany : c));

      setSuccess(`Company ${isCurrentlyActive ? 'deactivated' : 'activated'} successfully`);
      setToggleConfirmOpen(false);
      setCompanyToToggle(null);

      // Refresh stats as status change affects breakdown
      loadLicenceStats();
    } catch (error: any) {
      setError('Failed to update company status');
      console.error('Toggle status error:', error);
    } finally {
      setToggling(false);
    }
  };

  const handleToggleCancel = () => {
    setToggleConfirmOpen(false);
    setCompanyToToggle(null);
  };

  const handleRegenerateApiKey = async (company: Company) => {
    try {
      const result = await companyApi.regenerateApiKey(company.id);
      setCompanies(companies.map(c =>
        c.id === company.id ? { ...c, apiKey: result.apiKey } : c
      ));
      setSuccess(`API key regenerated: ${result.apiKey}`);
    } catch (error: any) {
      console.error('Failed to regenerate API key:', error);
      setError('Failed to regenerate API key');
    }
  };

  const handleManageUsers = (company: Company) => {
    setUserCompany(company);
    setUserManagerOpen(true);
  };

  // Transactions-management +/- handlers (MasterAdmin edit mode). The subtract
  // handlers guard against removing more than the current "Left" balance.
  const handleAddCalculations = () => {
    setFormData({
      ...formData,
      calculationsAllotted: (formData.calculationsAllotted || 0) + calcAdjust,
      calculationsLeft: (formData.calculationsLeft || 0) + calcAdjust,
    });
    setCalcAdjust(0);
  };
  const handleSubtractCalculations = () => {
    if (calcAdjust > (formData.calculationsLeft || 0)) {
      setError(`Cannot remove ${calcAdjust} — only ${formData.calculationsLeft || 0} calculations left`);
      return;
    }
    setFormData({
      ...formData,
      calculationsAllotted: (formData.calculationsAllotted || 0) - calcAdjust,
      calculationsLeft: (formData.calculationsLeft || 0) - calcAdjust,
    });
    setCalcAdjust(0);
  };
  const handleAddGeolocations = () => {
    setFormData({
      ...formData,
      geolocationAllotted: (formData.geolocationAllotted || 0) + geoAdjust,
      geolocationLeft: (formData.geolocationLeft || 0) + geoAdjust,
    });
    setGeoAdjust(0);
  };
  const handleSubtractGeolocations = () => {
    if (geoAdjust > (formData.geolocationLeft || 0)) {
      setError(`Cannot remove ${geoAdjust} — only ${formData.geolocationLeft || 0} geolocations left`);
      return;
    }
    setFormData({
      ...formData,
      geolocationAllotted: (formData.geolocationAllotted || 0) - geoAdjust,
      geolocationLeft: (formData.geolocationLeft || 0) - geoAdjust,
    });
    setGeoAdjust(0);
  };
  const handleAddUniqueShipments = () => {
    setFormData({
      ...formData,
      uniqueShipmentsAllotted: (formData.uniqueShipmentsAllotted || 0) + uniqueShipmentAdjust,
      uniqueShipmentsLeft: (formData.uniqueShipmentsLeft || 0) + uniqueShipmentAdjust,
    });
    setUniqueShipmentAdjust(0);
  };
  const handleSubtractUniqueShipments = () => {
    if (uniqueShipmentAdjust > (formData.uniqueShipmentsLeft || 0)) {
      setError(`Cannot remove ${uniqueShipmentAdjust} — only ${formData.uniqueShipmentsLeft || 0} unique shipments left`);
      return;
    }
    setFormData({
      ...formData,
      uniqueShipmentsAllotted: (formData.uniqueShipmentsAllotted || 0) - uniqueShipmentAdjust,
      uniqueShipmentsLeft: (formData.uniqueShipmentsLeft || 0) - uniqueShipmentAdjust,
    });
    setUniqueShipmentAdjust(0);
  };

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!companyToDelete) return;
    try {
      setDeleting(true);
      await companyApi.delete(companyToDelete.id);
      setSuccess(`Company "${companyToDelete.name}" has been deactivated successfully`);
      setDeleteConfirmOpen(false);
      setCompanyToDelete(null);
      await loadCompanies();
      loadLicenceStats();
    } catch (error: any) {
      setError('Failed to delete company');
      console.error('Delete company error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setCompanyToDelete(null);
  };

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };
  const sortableHeader = (field: string, label: string) => (
    <TableSortLabel
      active={sortBy === field}
      direction={sortBy === field ? sortDir : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const toggleActionLabel = companyToToggle?.disabled ? 'Activate' : 'Deactivate';

  return (
    <Box>
      <PageHeader
        description="Manage licensee accounts, their details, and feature access."
        actions={
          <CompaniesHeaderActions
            accountType={currentUser?.accountType}
            licenceStats={licenceStats}
            selectedLicenceType={selectedLicenceType}
            statusFilter={statusFilter}
            setStatusFilter={handleStatusFilterChange}
            currentStatusLabel={currentStatus.label}
            totalRows={totalRows}
            exporting={exporting}
            onTileClick={handleTileClick}
            onExport={handleExportLicenceType}
            onCreate={handleCreateClick}
          />
        }
      />

      {/* Licence Type Stats Cards */}
      <LicenceStatsCards
        licenceStats={licenceStats}
        selectedLicenceType={selectedLicenceType}
        exporting={exporting}
        onTileClick={handleTileClick}
        onExport={handleExportLicenceType}
      />

      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          backgroundColor: 'grey.50',
          borderRadius: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'grey.200',
        }}>
          <TextField
            placeholder="Search licensees by name, license type, ID, or API key..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1.5, color: 'primary.main' }} />,
            }}
            sx={{
              mb: 0,
              flex: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: 2,
                fontSize: '0.95rem',
                '& fieldset': {
                  borderColor: 'grey.300',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                },
              },
              '& .MuiInputBase-input': {
                py: 1.5,
              },
            }}
          />
          {searchTerm && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'primary.main',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
            }}>
              {totalRows} licensees
            </Box>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'id' ? sortDir : false}>
                {sortableHeader('id', 'ID')}
              </TableCell>
              <TableCell>Logo</TableCell>
              <TableCell sortDirection={sortBy === 'name' ? sortDir : false}>
                {sortableHeader('name', 'Licensee Name')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'licenceType' ? sortDir : false}>
                {sortableHeader('licenceType', 'Licence Type')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'licenceStart' ? sortDir : false}>
                {sortableHeader('licenceStart', 'Licence Start Date')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'licenceEnd' ? sortDir : false}>
                {sortableHeader('licenceEnd', 'Licence End Date')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'country' ? sortDir : false}>
                {sortableHeader('country', 'Country')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'partner' ? sortDir : false}>
                {sortableHeader('partner', 'Partner')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'analytics' ? sortDir : false}>
                {sortableHeader('analytics', 'Analytics')}
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <CompaniesTableBody
              loading={loading}
              companies={filteredCompanies}
              accountType={currentUser?.accountType}
              currentUserCompanyId={currentUser?.companyId}
              isBasicLicenceUser={isBasicLicenceUser}
              onManageUsers={handleManageUsers}
              onToggleStatus={handleToggleStatus}
              onDelete={handleDeleteClick}
              onEdit={handleEditClick}
              onView={handleViewClick}
              onRegenerateApiKey={handleRegenerateApiKey}
            />
          </TableBody>
        </Table>
        {!loading && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>

      {/* Create/Edit Dialog - Single Page Form */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 3,
            background: '#ffffff',
            border: '1px solid #e0e0e0',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
          }
        }}
      >
        <DialogTitle sx={{
          pb: 3,
          borderBottom: '1px solid #e0e0e0',
          background: '#ffffff',
          m: 0,
          mb: 0
        }}>
          <Typography variant="h4" component="h1" fontWeight="600" sx={{ color: '#333', mb: 1 }}>
            {companyDialogTitle(editingCompany, viewCompany)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            {editingCompany ? 'Update licensee information and settings' : 'Manage licensee and their access to the CarbonCare platform'}
          </Typography>
        </DialogTitle>
        <CompanyFormContent
          formData={formData}
          setFormData={setFormData}
          validationErrors={validationErrors}
          validateField={validateField}
          fieldsDisabled={fieldsDisabled}
          editingCompany={editingCompany}
          viewCompany={viewCompany}
          isHigherAdmin={isHigherAdmin}
          isLicenseeAdmin={isLicenseeAdmin}
          isMasterAdmin={isMasterAdmin}
          showApiKeyInCompanyDialog={showApiKeyInCompanyDialog}
          logoFile={logoFile}
          handleLogoFileChange={handleLogoFileChange}
          countries={countries}
          countryAliasMap={countryAliasMap}
          formatNumber={formatNumber}
          formatDateTimeLocal={formatDateTimeLocal}
          renderRemainingField={renderRemainingField}
          handleLicenceStartChange={handleLicenceStartChange}
          handleLicenceEndChange={handleLicenceEndChange}
          handleCalcAllottedChange={handleCalcAllottedChange}
          handleGeoAllottedChange={handleGeoAllottedChange}
          handleUniqueAllottedChange={handleUniqueAllottedChange}
          calcAdjust={calcAdjust}
          setCalcAdjust={setCalcAdjust}
          geoAdjust={geoAdjust}
          setGeoAdjust={setGeoAdjust}
          uniqueShipmentAdjust={uniqueShipmentAdjust}
          setUniqueShipmentAdjust={setUniqueShipmentAdjust}
          theme={theme}
          handleAddCalculations={handleAddCalculations}
          handleSubtractCalculations={handleSubtractCalculations}
          handleAddGeolocations={handleAddGeolocations}
          handleSubtractGeolocations={handleSubtractGeolocations}
          handleAddUniqueShipments={handleAddUniqueShipments}
          handleSubtractUniqueShipments={handleSubtractUniqueShipments}
        />
        <CompanyDialogActions
          saving={saving}
          viewCompany={viewCompany}
          editingCompany={editingCompany}
          submitDisabled={saving || (!!editingCompany && !isHigherAdmin && !isLicenseeAdmin)}
          formComplete={isFormComplete()}
          onClose={() => setOpenDialog(false)}
          onSubmit={handleSubmit}
        />
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={8000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={8000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!info}
        autoHideDuration={5000}
        onClose={() => setInfo(null)}
      >
        <Alert severity="info" onClose={() => setInfo(null)}>
          {info}
        </Alert>
      </Snackbar>

      {/* User Manager */}
      {userCompany && (
        <UserManager
          companyId={userCompany.id}
          companyName={userCompany.name}
          open={userManagerOpen}
          onClose={() => {
            setUserManagerOpen(false);
            setOpenAddUserForNewCompany(false);
          }}
          initialOpenAddDialog={openAddUserForNewCompany}
          initialAccountType={AccountType.LicenseeAdmin}
        />
      )}

      {/* Toggle Status (Active/Inactive) Confirmation Dialog */}
      <ToggleStatusDialog
        open={toggleConfirmOpen}
        company={companyToToggle}
        toggling={toggling}
        actionLabel={toggleActionLabel}
        onCancel={handleToggleCancel}
        onConfirm={handleToggleConfirm}
      />

      {/* Delete (Deactivate) Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="600" color="error">
            Delete Licensee
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Are you sure you want to <strong>delete</strong>{' '}
            {companyToDelete?.name}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={handleDeleteCancel} variant="outlined" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={<Delete />}
          >
            {deleting ? 'Deleting...' : 'Yes, Delete Licensee'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniesPage;